import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, BedDouble, Bath, Ruler, MapPin } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

// Property prices are stored in SGD and shown to clients in CHF.
import { useSgdChfRate, sgdToChf } from "@/lib/currency";

export default function Properties() {
  const chfRate = useSgdChfRate();
  const fmt = (n) => sgdToChf(n || 0, chfRate);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", district: "", price_sgd: "", bedrooms: 3, bathrooms: 2, sqft: 1200, property_type: "Condominium", image_url: "", description: "", absd_savings_pct: 20 });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/properties");
      setItems(res.data);
    } catch (_e) {
      toast.error("Failed to load properties");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title || !form.district || !form.price_sgd) { toast.error("Title, district, price required"); return; }
    try {
      await api.post("/properties", {
        ...form,
        price_sgd: Number(form.price_sgd),
        bedrooms: Number(form.bedrooms),
        bathrooms: Number(form.bathrooms),
        sqft: Number(form.sqft),
        absd_savings_pct: Number(form.absd_savings_pct),
      });
      toast.success("Property added");
      setOpen(false);
      setForm({ title: "", district: "", price_sgd: "", bedrooms: 3, bathrooms: 2, sqft: 1200, property_type: "Condominium", image_url: "", description: "", absd_savings_pct: 20 });
      load();
    } catch (_e) { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove listing?")) return;
    await api.delete(`/properties/${id}`);
    load();
  };

  return (
    <div className="p-8 lg:p-10">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="label-eyebrow">Catalog</div>
          <h1 className="font-serif-display text-4xl text-[#0A1128] mt-2 tracking-tight">Prime residential listings</h1>
          <p className="text-slate-500 mt-2 text-sm">Curated Singapore properties for our Swiss investors.</p>
        </div>
        <Button data-testid="new-property-btn" onClick={() => setOpen(true)} className="bg-[#0A1128] text-white hover:bg-[#152243]"><Plus className="w-4 h-4 mr-2" /> Add listing</Button>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
        {loading ? (
          [...Array(6)].map((_, i) => (
            <div key={i} className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden space-y-4 p-4">
              <Skeleton className="h-48 w-full rounded-md" />
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <div className="flex gap-3 pt-2">
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-4 w-16" />
              </div>
            </div>
          ))
        ) : items.length === 0 ? (
          <div className="col-span-full text-slate-500 text-sm">No listings yet. Seed demo data from Overview or add one.</div>
        ) : (
          items.map((p) => (
          <div key={p.property_id} data-testid={`property-${p.property_id}`} className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden hover-lift">
            <div className="h-48 bg-slate-100 relative overflow-hidden">
              {p.image_url && <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />}
              <div className="absolute top-3 left-3 bg-[#0A1128]/90 text-[#C5A880] text-[10px] tracking-widest uppercase px-2.5 py-1 rounded-full backdrop-blur">
                ABSD saving ~{p.absd_savings_pct}%
              </div>
            </div>
            <div className="p-5">
              <div className="font-serif-display text-lg text-[#0A1128] leading-tight">{p.title}</div>
              <div className="flex items-center gap-1 text-xs text-slate-500 mt-1"><MapPin className="w-3 h-3" /> {p.district}</div>
              <div className="mt-3 flex items-center gap-4 text-xs text-slate-600">
                <span className="flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" /> {p.bedrooms}</span>
                <span className="flex items-center gap-1"><Bath className="w-3.5 h-3.5" /> {p.bathrooms}</span>
                <span className="flex items-center gap-1"><Ruler className="w-3.5 h-3.5" /> {p.sqft} sqft</span>
                <a
                  href={`https://plus.sri.sg/?q=${encodeURIComponent(p.title + ' ' + p.district)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="ml-auto text-[10px] tracking-widest uppercase text-[#C5A880] hover:text-[#8A6B33]"
                  data-testid={`sri-link-${p.property_id}`}
                >
                  SRI Polaris ↗
                </a>
              </div>
              <div className="mt-4 flex items-end justify-between">
                <div>
                  <div className="label-eyebrow text-[10px]">Asking</div>
                  <div className="font-serif-display text-xl text-[#0A1128]">{fmt(p.price_sgd)}</div>
                </div>
                <Button size="icon" variant="ghost" onClick={() => del(p.property_id)}><Trash2 className="w-4 h-4 text-slate-500" /></Button>
              </div>
              {p.description && <p className="text-xs text-slate-600 mt-3 line-clamp-2">{p.description}</p>}
            </div>
          </div>
        )))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl">Add listing</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2"><Label>Title*</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label>District*</Label><Input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} /></div>
            <div><Label>Type</Label><Input value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} /></div>
            <div><Label>Price SGD*</Label><Input type="number" value={form.price_sgd} onChange={(e) => setForm({ ...form, price_sgd: e.target.value })} /></div>
            <div><Label>Sqft</Label><Input type="number" value={form.sqft} onChange={(e) => setForm({ ...form, sqft: e.target.value })} /></div>
            <div><Label>Bedrooms</Label><Input type="number" value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} /></div>
            <div><Label>Bathrooms</Label><Input type="number" value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} /></div>
            <div className="col-span-2"><Label>Image URL</Label><Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} /></div>
            <div className="col-span-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="bg-[#0A1128] text-white hover:bg-[#152243]">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
